import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { CardLayout, DeleteModal, MTable } from "../../v3";
import { getCategoryTableColumns } from "../../../pages/library/category/constants";
import { DELETE_FINANCE_CATEGORY, EDIT_FINANCE_CATEGORY } from "../../../constants";
import { FinanceService } from "../../../services";
import { AddEditCategory } from "./AddEditCategory.component";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../../utils";
import { handlePdfDownload, Listing } from "../../pdf";

export const CategoryList = ({ totalCount = 0, setTotalCount = () => {}, onSuccess = () => {}, reloadData = 0 }) => {
  const [categories, setCategories] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [categoryId, setCategoryId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [sortModel, setSortModel] = useState([{ field: "updatedAt", sort: "desc" }]);
  const [paginationState, setPaginationState] = useState({
    page: 0,
    pageSize: 10,
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    categoryId: null,
  });

  const { user } = useSelector((state) => state.user);

  const hasEditCategory = useHasPermission(EDIT_FINANCE_CATEGORY);
  const hasDeleteCategory = useHasPermission(DELETE_FINANCE_CATEGORY);

  const fetchData = useCallback(
    async (categoryFilters = {}) => {
      try {
        setLoading(true);
        const response = await FinanceService.getCategories(categoryFilters);
        setCategories(response?.data?.rows);
        setTotalCount(response?.data?.count);
      } catch (error) {
        console.log("🚀 ~ CategoryList ~ error:", error);
      } finally {
        setLoading(false);
      }
    },
    [setTotalCount],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      name: searchText,
    });
  }, [fetchData, sortModel, paginationState, searchText, reloadData]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title={"Category"} columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Category",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handleCloseModal = () => setOpenModal(false);

  const openDeleteModal = (categoryData) => {
    setDeleteModal({
      isOpen: true,
      categoryId: categoryData.id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, categoryId: null });
  };

  const handleDelete = async () => {
    try {
      await FinanceService.deleteCategory(deleteModal.categoryId);
      setDeleteModal({ isOpen: false, leadId: null });
      toast.success("Category deleted successfully");
      onSuccess?.();
    } catch (error) {
      console.log("🚀 ~ handleDelete ~ error:", error);
      toast.error("Failed to delete Category");
    }
  };

  const handleActions = (categoryData, actionType) => {
    switch (actionType) {
      case "edit":
        setCategoryId(categoryData?.id);
        setOpenModal(true);
        break;
      case "delete":
        openDeleteModal(categoryData);
        break;
      default:
        break;
    }
  };

  const columnHeaders = getCategoryTableColumns(handleActions, hasEditCategory, hasDeleteCategory);

  const rows = categories.map((category, index) => ({
    sl: index + 1 + paginationState.page * paginationState.pageSize,
    id: category.id,
    name: category.name,
    type: category.type,
    description: category.description,
  }));

  return (
    <>
      <CardLayout>
        <MTable
          // Search data
          placeholder="Search category"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          // Export data
          showExportButton // TODO: implement permission
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
        />
      </CardLayout>

      {/* edit category modal */}
      <AddEditCategory open={openModal} categoryId={categoryId} onClose={handleCloseModal} onSuccess={onSuccess} />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this category?"
      />
    </>
  );
};
